/**
 * AgendaPreview — defesa em profundidade pro conflito de horário (UX-3 / FEAT-V2-018).
 *
 * Renderiza mini-timeline visual do dia + banner verde/amarelo indicando se
 * intervalo selecionado conflita com eventos existentes.
 *
 * Backend continua checando freebusy no POST como defesa final.
 *
 * Props:
 *   - startIso: ISO datetime do início do intervalo (null = oculta preview)
 *   - endIso: ISO datetime do fim. Pode ser null quando timeMode='start_only'
 *             (assume 30 min default).
 *
 * Cache 30s via useCalendarRange existente — evita spam de requests quando
 * user digita horários rapidamente.
 */

import { useMemo } from 'react';

import { Alert } from '@/components/ui';
import { useCalendarRange, type CalendarEvent } from '@/lib/home-today-store';

import { formatTime } from '@/lib/format-date';
interface AgendaPreviewProps {
  startIso: string | null;
  endIso: string | null;
  /** Default 30 min quando endIso=null (timeMode=start_only). */
  defaultDurationMin?: number;
}

function startOfDayIso(iso: string): string {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function endOfDayIso(iso: string): string {
  const d = new Date(iso);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

function fmtTime(iso: string): string {
  try {
    return formatTime(iso);
  } catch {
    return '--:--';
  }
}

/**
 * Detecta sobreposição entre 2 intervalos [aStart, aEnd] vs [bStart, bEnd].
 * Idêntica à logic do backend pra consistência.
 */
function overlaps(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd;
}

export function AgendaPreview({ startIso, endIso, defaultDurationMin = 30 }: AgendaPreviewProps) {
  // Calcula range do dia (start até end do mesmo dia).
  const dayRange = useMemo(() => {
    if (!startIso) return { fromIso: null, toIso: null };
    return {
      fromIso: startOfDayIso(startIso),
      toIso: endOfDayIso(startIso),
    };
  }, [startIso]);

  const { data, isLoading, isError } = useCalendarRange(dayRange.fromIso, dayRange.toIso);

  const conflictingEvents = useMemo<CalendarEvent[]>(() => {
    if (!data?.events || !startIso) return [];
    const startMs = Date.parse(startIso);
    const endMs = endIso
      ? Date.parse(endIso)
      : startMs + defaultDurationMin * 60_000;
    if (isNaN(startMs) || isNaN(endMs)) return [];

    return data.events.filter((e) => {
      const eStart = e.start.dateTime ? Date.parse(e.start.dateTime) : NaN;
      const eEnd = e.end.dateTime ? Date.parse(e.end.dateTime) : NaN;
      if (isNaN(eStart) || isNaN(eEnd)) return false;
      return overlaps(startMs, endMs, eStart, eEnd);
    });
  }, [data, startIso, endIso, defaultDurationMin]);

  if (!startIso) return null;

  if (isLoading) {
    return (
      <div
        data-testid="agenda-preview-loading"
        className="text-xs text-ink-3 dark:text-ink-dark-3 italic py-2"
      >
        Consultando agenda do dia…
      </div>
    );
  }

  if (isError) {
    return (
      <div
        data-testid="agenda-preview-error"
        className="text-xs text-ink-3 dark:text-ink-dark-3 italic py-2"
      >
        Não foi possível verificar a agenda — pode salvar mesmo assim.
      </div>
    );
  }

  const eventCount = data?.events.length ?? 0;
  const hasConflict = conflictingEvents.length > 0;

  return (
    <div data-testid="agenda-preview" className="space-y-2">
      {/* Mini-timeline do dia (eventos existentes) */}
      {eventCount > 0 && (
        <div data-testid="agenda-preview-day-events" className="space-y-1">
          <p className="text-xs font-medium text-ink-3 dark:text-ink-dark-3 uppercase tracking-wider">
            {eventCount} compromisso(s) no dia
          </p>
          <ul className="space-y-0.5 max-h-24 overflow-y-auto">
            {data!.events.map((e) => {
              const isConflicting = conflictingEvents.some((c) => c.id === e.id);
              return (
                <li
                  key={e.id}
                  data-testid={`agenda-preview-event-${e.id}`}
                  data-conflicting={isConflicting ? 'true' : 'false'}
                  className={`text-xs flex gap-2 ${isConflicting ? 'text-red font-medium' : 'text-ink-2 dark:text-ink-dark-2'}`}
                >
                  <span className="font-mono tabular-nums shrink-0">
                    {fmtTime(e.start.dateTime)}–{fmtTime(e.end.dateTime)}
                  </span>
                  <span className="truncate">{e.summary}</span>
                  {isConflicting && <span aria-hidden="true">⚠️</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Banner status */}
      {hasConflict ? (
        <Alert variant="warning" rail data-testid="agenda-preview-conflict">
          <p className="text-xs font-medium">
            ⚠️ Conflito com {conflictingEvents.length} compromisso(s)
          </p>
          <p className="text-xs mt-1">
            Você pode salvar mesmo assim — o backend confirmará a defesa final.
          </p>
        </Alert>
      ) : (
        <p
          data-testid="agenda-preview-free"
          className="text-xs text-success font-medium py-1"
        >
          ✓ Horário livre
        </p>
      )}
    </div>
  );
}
